import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  IconButton
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

interface Expense {
  id: number;
  expense_number: string;
  employee_id: number;
  employee_name: string;
  employee_email?: string;
  category_id: number;
  category_name: string;
  expense_date: string;
  description: string;
  net_amount: number;
  vat_rate: string;
  vat_amount: number;
  total_amount: number;
  status: string;
  merchant_name?: string;
  business_purpose?: string;
  notes?: string;
  receipt_url?: string;
  submitted_date?: string;
  approved_date?: string;
  approved_by?: string;
  net_amount_formatted: string;
  vat_amount_formatted: string;
  total_amount_formatted: string;
}

interface ViewExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  expense: Expense | null;
  onApprove?: (expenseId: number) => void;
  onReject?: (expenseId: number) => void;
}

const ViewExpenseDialog: React.FC<ViewExpenseDialogProps> = ({
  open,
  onClose,
  expense,
  onApprove,
  onReject
}) => {
  if (!expense) return null;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'default';
      case 'submitted': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'paid': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return '📝';
      case 'submitted': return '📤';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'paid': return '💰';
      default: return '📄';
    }
  };

  const formatVATRate = (vatRate: string) => {
    switch (vatRate) {
      case 'STANDARD': return '20%';
      case 'REDUCED': return '5%';
      case 'ZERO': return '0%';
      case 'EXEMPT': return 'Exempt';
      default: return vatRate;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ViewIcon />
            Expense Details - {expense.expense_number}
          </Box>
          <Chip
            label={`${getStatusIcon(expense.status)} ${expense.status.toUpperCase()}`}
            color={getStatusColor(expense.status) as any}
            size="small"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Employee Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Employee Information
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 56, height: 56 }}>
                {expense.employee_name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Box>
                <Typography variant="h6">{expense.employee_name}</Typography>
                {expense.employee_email && (
                  <Typography variant="body2" color="textSecondary">
                    {expense.employee_email}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Expense Information */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Expense Details
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Category:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {expense.category_name}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Date:</Typography>
                  <Typography variant="body2">
                    {new Date(expense.expense_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Box>
                {expense.merchant_name && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Merchant:</Typography>
                    <Typography variant="body2">{expense.merchant_name}</Typography>
                  </Box>
                )}
                {expense.business_purpose && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Purpose:</Typography>
                    <Typography variant="body2">{expense.business_purpose}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Financial Details
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Net Amount:</Typography>
                  <Typography variant="body2">{expense.net_amount_formatted}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">VAT Rate:</Typography>
                  <Typography variant="body2">{formatVATRate(expense.vat_rate)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">VAT Amount:</Typography>
                  <Typography variant="body2">{expense.vat_amount_formatted}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total Amount:</Typography>
                  <Typography variant="h6" color="primary">
                    {expense.total_amount_formatted}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Description */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
              {expense.description}
            </Typography>
          </CardContent>
        </Card>

        {/* Receipt */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Receipt
              </Typography>
              {expense.receipt_url && (
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  size="small"
                >
                  Download
                </Button>
              )}
            </Box>
            
            {expense.receipt_url ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'grey.50'
                }}
              >
                <ReceiptIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2">Receipt Available</Typography>
                <Typography variant="caption" color="textSecondary">
                  Click download to view the receipt
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <ReceiptIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  No receipt uploaded
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Additional Notes */}
        {expense.notes && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Notes
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {expense.notes}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Status Information */}
        <Card sx={{ 
          mb: 2,
          bgcolor: expense.status === 'approved' ? 'success.light' : 
                  expense.status === 'rejected' ? 'error.light' : 
                  expense.status === 'submitted' ? 'info.light' : 'grey.100'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Status Information
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body1">
                  Current Status: <strong>{expense.status.toUpperCase()}</strong>
                </Typography>
                {expense.submitted_date && (
                  <Typography variant="body2" color="textSecondary">
                    Submitted: {new Date(expense.submitted_date).toLocaleDateString('en-GB')}
                  </Typography>
                )}
                {expense.approved_date && (
                  <Typography variant="body2" color="textSecondary">
                    Approved: {new Date(expense.approved_date).toLocaleDateString('en-GB')}
                  </Typography>
                )}
                {expense.approved_by && (
                  <Typography variant="body2" color="textSecondary">
                    Approved by: {expense.approved_by}
                  </Typography>
                )}
              </Box>
              
              {expense.status === 'submitted' && (
                <Box>
                  <Button
                    startIcon={<ApproveIcon />}
                    variant="contained"
                    color="success"
                    size="small"
                    sx={{ mr: 1 }}
                    onClick={() => onApprove?.(expense.id)}
                  >
                    Approve
                  </Button>
                  <Button
                    startIcon={<RejectIcon />}
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => onReject?.(expense.id)}
                  >
                    Reject
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {expense.status === 'draft' && (
          <Button startIcon={<EditIcon />} variant="outlined">
            Edit Expense
          </Button>
        )}
        <Button startIcon={<DownloadIcon />} variant="outlined">
          Export PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewExpenseDialog;
